<script setup lang="ts">
import { Copy, Trash2 } from 'lucide-vue-next'
import VueJsonPretty from 'vue-json-pretty'
import 'vue-json-pretty/lib/styles.css'
import { toast } from 'vue-sonner'
import { useErrorStore, type ErrorEntry } from '@/store/errorStore'

const props = defineProps<{
  entry: ErrorEntry
}>()

const emit = defineEmits<{
  (e: 'close'): void
}>()

const errorStore = useErrorStore()

function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleString()
}

async function copyAsJson() {
  try {
    await navigator.clipboard.writeText(JSON.stringify(props.entry, null, 2))
    toast.success('Copied error details to clipboard')
  } catch {
    toast.error('Failed to copy to clipboard')
  }
}

function dismiss() {
  errorStore.dismissError(props.entry.id)
  emit('close')
}
</script>

<template>
  <div class="space-y-4 p-4 text-sm">
    <!-- Header actions -->
    <div class="flex items-center justify-end gap-2">
      <button
        class="inline-flex items-center gap-1.5 rounded-md bg-zinc-100 px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700"
        @click="copyAsJson"
      >
        <Copy class="h-3.5 w-3.5" />
        Copy as JSON
      </button>
      <button
        class="inline-flex items-center gap-1.5 rounded-md bg-red-50 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/40"
        @click="dismiss"
      >
        <Trash2 class="h-3.5 w-3.5" />
        Dismiss
      </button>
    </div>

    <!-- Error message -->
    <div>
      <h4 class="mb-1 text-xs font-semibold uppercase tracking-wide text-zinc-500">Error Message</h4>
      <p class="rounded-md bg-red-50 p-2 text-red-700 dark:bg-red-900/20 dark:text-red-300">
        {{ entry.errorMessage }}
      </p>
    </div>

    <!-- HTTP details -->
    <div class="grid grid-cols-2 gap-3">
      <div>
        <h4 class="mb-1 text-xs font-semibold uppercase tracking-wide text-zinc-500">Status Code</h4>
        <span class="font-mono font-semibold">{{ entry.httpStatusCode }}</span>
      </div>
      <div>
        <h4 class="mb-1 text-xs font-semibold uppercase tracking-wide text-zinc-500">HTTP Method</h4>
        <span class="font-mono font-semibold">{{ entry.httpMethod }}</span>
      </div>
      <div class="col-span-2">
        <h4 class="mb-1 text-xs font-semibold uppercase tracking-wide text-zinc-500">Request URL</h4>
        <span class="break-all font-mono text-xs">{{ entry.requestUrl }}</span>
      </div>
      <div class="col-span-2">
        <h4 class="mb-1 text-xs font-semibold uppercase tracking-wide text-zinc-500">Captured At</h4>
        <span class="text-xs">{{ formatTimestamp(entry.capturedAt) }}</span>
      </div>
    </div>

    <!-- Response body -->
    <div>
      <h4 class="mb-1 text-xs font-semibold uppercase tracking-wide text-zinc-500">Response Body</h4>
      <div class="max-h-60 overflow-auto rounded-md border border-zinc-200 p-2 dark:border-zinc-700">
        <VueJsonPretty :data="(entry.responseBody as any)" :deep="2" :showLine="false" />
      </div>
    </div>

    <!-- API Diagnostics -->
    <div>
      <h4 class="mb-1 text-xs font-semibold uppercase tracking-wide text-zinc-500">API Diagnostics</h4>
      <div v-if="entry.apiDiagnostics" class="grid grid-cols-2 gap-2 rounded-md border border-zinc-200 p-3 dark:border-zinc-700">
        <div>
          <span class="text-xs text-zinc-500">Status Code</span>
          <p class="font-mono text-xs font-semibold">{{ entry.apiDiagnostics.statusCode ?? '—' }}</p>
        </div>
        <div>
          <span class="text-xs text-zinc-500">Timestamp</span>
          <p class="font-mono text-xs">{{ entry.apiDiagnostics.timestamp ?? '—' }}</p>
        </div>
        <div>
          <span class="text-xs text-zinc-500">Start Time</span>
          <p class="font-mono text-xs">{{ entry.apiDiagnostics.startTime ?? '—' }}</p>
        </div>
        <div>
          <span class="text-xs text-zinc-500">End Time</span>
          <p class="font-mono text-xs">{{ entry.apiDiagnostics.endTime ?? '—' }}</p>
        </div>
        <div class="col-span-2">
          <span class="text-xs text-zinc-500">Duration</span>
          <p class="font-mono text-xs font-semibold">{{ entry.apiDiagnostics.duration ?? '—' }}</p>
        </div>
      </div>
      <p v-else class="rounded-md bg-zinc-50 p-2 text-xs italic text-zinc-400 dark:bg-zinc-800">
        No API diagnostics available
      </p>
    </div>
  </div>
</template>
